import React, { useCallback } from "react";
import PriorityMarker from "./PriorityMarker";
import StartDate from "./StartDate";
import TaskMenu from "./TaskMenu";
import { Task } from "records/Task";
import { useTranslation } from "react-i18next";
import DueDate from "./DueDate";
import CardColumn from "components/CardColumn";
import addSentryBreadcrumb from "sentry/addSentryBreadcrumb";
import { useGetConvertedUserTime } from "hooks/useTimezone";

interface Props extends Task {
	onReadUnreadClick: (id: string, read: boolean) => void;
	changePriority: (id: string, priority: Task["priority"]) => void;
	unassignTask: (id: string) => void;
	canBeUnassigned: boolean;
	canBeReassigned: boolean;
}

function MyTaskCard({
	id,
	taskName,
	taskRead,
	workflowDefinitionName,
	activityStarter,
	priority,
	taskDueDate,
	createdTime,
	onReadUnreadClick,
	changePriority,
	canBeUnassigned,
	canBeReassigned,
	unassignTask,
	canDeleteActivity,
	deleteActivity,
}: Props) {
	const { t } = useTranslation();

	const createdTimeConverted = useGetConvertedUserTime(createdTime);

	const taskDueDateConverted = useGetConvertedUserTime(taskDueDate);

	/**
	 * Changing the priority
	 */
	const handlePriorityChange = useCallback(
		(e: React.SyntheticEvent<HTMLButtonElement>) => {
			addSentryBreadcrumb({
				category: "button.click",
				message: "Change priority",
			});
			changePriority(id, e.currentTarget.value as Task["priority"]);
		},
		[changePriority, id]
	);

	/**
	 * Toggle read/unread
	 */
	const handleMarkReadUnreadClick = useCallback(() => {
		addSentryBreadcrumb({
			category: "button.click",
			message: "Change read/unread status",
		});
		onReadUnreadClick(id, taskRead);
	}, [id, onReadUnreadClick, taskRead]);

	/**
	 * Unassign a task
	 */
	const handleUnassignTask = useCallback(() => {
		addSentryBreadcrumb({
			category: "button.click",
			message: "Unassign a task",
		});
		unassignTask(id);
	}, [id, unassignTask]);

	/**
	 * Delete an activity
	 */
	const handleDeleteActivity = useCallback(() => {
		addSentryBreadcrumb({
			category: "button.click",
			message: "Delete a task",
		});
		deleteActivity(id);
	}, [id, deleteActivity]);

	return (
		<>
			<CardColumn className="task-card__title" width="xl">
				{taskName}
			</CardColumn>
			<CardColumn className="task-card__activity" width="xl">
				{workflowDefinitionName}
			</CardColumn>
			<CardColumn className="task-card__date--start" width="sm">
				<StartDate date={createdTimeConverted} />
			</CardColumn>
			<CardColumn className="task-card__date--due" width="sm">
				<DueDate date={taskDueDateConverted} />
			</CardColumn>
			<CardColumn className="task-card__priority" width="xs">
				<PriorityMarker priority={priority} />
			</CardColumn>
			<CardColumn className="task-card__activity--starter" width="sm">
				{activityStarter ? activityStarter : t("main.text.na")}
			</CardColumn>
			<CardColumn className="task-card__menu">
				<TaskMenu
					canDeleteActivity={canDeleteActivity}
					deleteActivity={handleDeleteActivity}
					changePriority={handlePriorityChange}
					id={id}
					prioritySet={priority !== "NORMAL"}
					canBeReassigned={canBeReassigned}
					canBeUnassigned={canBeUnassigned}
					unassignTask={handleUnassignTask}
					onReadUnread={handleMarkReadUnreadClick}
				/>
			</CardColumn>
		</>
	);
}

export default MyTaskCard;
